import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';

import { Dialog, DialogProps, Tabs, Tab } from '@material-ui/core';
import useTranslation from 'next-translate/useTranslation';
import { ParsedUrlQueryInput } from 'querystring';
import { useGenres, useCountries, SwiperFilter, style } from '../api';
import Flex from './common/Flex';
import { TextRegular } from './common/Text';
import BPMFilter from './Filter/BPMFilter';
import Box from './common/Box';
import MultiSelectList from './common/MultiSelectList';
import { Check, ArrowBack } from './common/Icons';
import { useExploreQuery } from './ExploreFilter';

type TabPanelProps = React.ComponentProps<typeof Box>;

const TabPanel = ({ children, index, value, ...props }: TabPanelProps) => (
  <Box
    id={`filter-tab-${index}`}
    display={value !== index ? 'none' : undefined}
    overflow="auto"
    {...props}
  >
    {children}
  </Box>
);

const StyledTab = styled(Tab)`
  && {
    box-shadow: none;
    max-width: 100%;
    text-transform: none;
    width: 100%;
    min-width: initial;
  }
  &.Mui-selected {
    background-color: #f5f5f5;
    ${TextRegular} {
      font-weight: bold;
    }
  }
`;

const ActionButton = styled(TextRegular)`
  text-transform: uppercase;
  font-weight: bold;
  &:hover {
    cursor: pointer;
  }
`;

const ExploreFilterDialog = ({
  onApply,
  ...props
}: Omit<DialogProps, 'children'> & {
  onApply: () => void;
}) => {
  const { t } = useTranslation('common');
  const genres = useGenres({ withActiveTracksOnly: true });
  const { result: countries } = useCountries();
  const { push, pathname } = useRouter();

  const queryParsed = useExploreQuery();

  const [filters, setFilters] = useState<SwiperFilter>({
    genre: [],
    country: [],
  });

  const [tab, setTab] = useState(0);

  const setFilter = useCallback(
    (category: keyof SwiperFilter, value: SwiperFilter[keyof SwiperFilter]) => {
      setFilters((cur) => ({
        ...cur,
        [category]: value,
      }));
    },
    [],
  );

  const resetFilter = () => {
    setFilters({
      genre: [],
      country: [],
    });

    push(pathname, undefined, {
      shallow: true,
    });
  };

  const getFilter = useCallback(
    (category: keyof SwiperFilter): SwiperFilter[keyof SwiperFilter] =>
      filters && filters[category],
    [filters],
  );

  const applyFilters = (params: SwiperFilter = {}) => {
    push(
      {
        pathname,
        query: params as ParsedUrlQueryInput,
      },
      undefined,
      {
        shallow: true,
      },
    );

    onApply();
  };

  useEffect(() => {
    setFilters(omitBy(queryParsed, isEmpty));
  }, [queryParsed]);

  const onBPMValues = (values: { minValue: number; maxValue: number }) => {
    if (getFilter('min_bpm') !== values.minValue) {
      setFilter('min_bpm', values.minValue);
    }
    if (getFilter('max_bpm') !== values.maxValue) {
      setFilter('max_bpm', values.maxValue);
    }
  };

  return (
    <Dialog scroll="paper" {...props}>
      <Flex
        position="relative"
        maxHeight="100vh"
        maxWidth="100%"
        flexDirection="column"
      >
        <Flex
          p={3}
          width="100%"
          height="54px"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center">
            <Box mr={4} onClick={() => onApply()}>
              <ArrowBack size={22} />
            </Box>
            <TextRegular m={0} as="h2" fontWeight="bold" fontSize="18px">
              {t('common:filter') as string}
            </TextRegular>
          </Flex>
          <Flex>
            <ActionButton
              mr={2}
              color={style.grey_main_new}
              onClick={() => resetFilter()}
            >
              {t('common:reset') as string}
            </ActionButton>
            <ActionButton onClick={() => applyFilters(filters)}>
              {t('common:apply') as string}
            </ActionButton>
          </Flex>
        </Flex>
        <Flex flex={1} overflow="auto">
          <Box
            width="145px"
            height="100%"
            position="relative"
            borderRight="1px solid"
            borderColor={style.grey_light_1_new}
          >
            <Tabs
              orientation="vertical"
              value={tab}
              onChange={(_event, newValue) => setTab(newValue)}
              TabIndicatorProps={{ children: null, style: {} }}
            >
              <StyledTab
                disableRipple
                label={
                  <Flex
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <TextRegular>{t('common:genres') as string}</TextRegular>
                    {Boolean(filters.genre?.length) && (
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        bg={style.primary_pink_new}
                        size="12px"
                      >
                        <TextRegular color={style.white} fontSize="10px">
                          {filters.genre?.length}
                        </TextRegular>
                      </Flex>
                    )}
                  </Flex>
                }
              />
              <StyledTab
                disableRipple
                label={
                  <Flex
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <TextRegular>{t('common:locations') as string}</TextRegular>
                    {Boolean(filters.country?.length) && (
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        bg={style.primary_pink_new}
                        size="12px"
                      >
                        <TextRegular color={style.white} fontSize="10px">
                          {filters.country?.length}
                        </TextRegular>
                      </Flex>
                    )}
                  </Flex>
                }
              />
              <StyledTab
                disableRipple
                label={
                  <Flex
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <TextRegular>{t('common:bpm') as string}</TextRegular>
                    {Boolean(filters.min_bpm || filters.max_bpm) && (
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        bg={style.primary_pink_new}
                        size="12px"
                      >
                        <TextRegular color={style.white} fontSize="10px">
                          -
                        </TextRegular>
                      </Flex>
                    )}
                  </Flex>
                }
              />
            </Tabs>
          </Box>
          <Box
            width="100%"
            maxHeight="100%"
            overflow="auto"
            position="relative"
          >
            <TabPanel px={3} value={tab} index={0}>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                py={3}
                onClick={() => setFilter('genre', [])}
                fontWeight={!filters.genre?.length ? 'bold' : 'initial'}
              >
                {t('common:all') as string}
                <Box color={style.primary_pink_new} size="18px">
                  {!filters.genre?.length && <Check size={18} />}
                </Box>
              </Flex>
              <MultiSelectList
                selected={filters.genre as string[] | number[]}
                onChange={(items) => {
                  setFilter('genre', items as number[]);
                }}
                options={genres.map((genre) => ({
                  label: genre.name,
                  value: genre.id,
                }))}
              />
            </TabPanel>
            <TabPanel px={3} value={tab} index={1}>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                py={3}
                onClick={() => setFilter('country', [])}
                fontWeight={!filters.country?.length ? 'bold' : 'initial'}
              >
                {t('common:all') as string}
                <Box color={style.primary_pink_new} size="18px">
                  {!filters.country?.length && <Check size={18} />}
                </Box>
              </Flex>
              <MultiSelectList
                selected={filters.country as string[] | number[]}
                onChange={(items) => {
                  setFilter('country', items as number[]);
                }}
                options={countries.map((genre) => ({
                  label: genre.name,
                  value: genre.id,
                }))}
              />
            </TabPanel>
            <TabPanel px={4} py={3} value={tab} index={2}>
              <BPMFilter onChange={(values) => onBPMValues(values)} />
            </TabPanel>
          </Box>
        </Flex>
      </Flex>
    </Dialog>
  );
};

export default ExploreFilterDialog;
